/**
 * Popup Coordinator — ensures only one popup is active at a time.
 *
 * Three popups compete for attention:
 *   1. ExitIntentPopup (£100 voucher + Grand Designs tickets)
 *   2. ChatWidget (AI text chat, bottom-right)
 *   3. AnamWidget / Priya (AI video avatar, bottom-left)
 *
 * Rules:
 *   - Whichever popup the user engages with first takes priority.
 *   - While one popup is active, the other two must NOT auto-open.
 *   - Manual user clicks on chat bubble / Priya orb still work (user intent overrides).
 *   - When the active popup is closed, the lock is released so others can trigger.
 *
 * Implementation:
 *   Simple global variable (not React state) so all three independent components
 *   can read/write without prop drilling or context re-renders.
 */

export type PopupId = "voucher" | "chat" | "priya" | "campaign-exit" | null;

let activePopup: PopupId = null;

/** Attempt to claim the popup slot. Returns true if granted. */
export function claimPopup(id: PopupId): boolean {
  if (activePopup === null || activePopup === id) {
    activePopup = id;
    return true;
  }
  return false;
}

/** Release the popup slot so others can trigger. */
export function releasePopup(id: PopupId): void {
  if (activePopup === id) {
    activePopup = null;
  }
}

/** Check if any popup is currently active. */
export function isAnyPopupActive(): boolean {
  return activePopup !== null;
}

/** Get which popup is currently active. */
export function getActivePopup(): PopupId {
  return activePopup;
}
